import { Amenity, IFiltersState, PropertyType, Seller } from 'components/filters/types';
import config from 'config';
import head from 'lodash/head';
import { ITranslate } from 'locale';
import { IAutocompleteEntry, CompletionType, DealType } from 'utils/entities';
import { BreakpointValue } from 'consts/breakpoints';
import { SearchViewMode } from 'components/list-view-switcher/constants';
import { SchoolLevel } from 'store/sagas/apiService/types';

export enum ComplexPropertyType {
  Projects = 'projects',
  VillaOrCottage = 'villaOrCottage',
  MultiplePropertyType = 'multiplePropertyType',
}

type HomeTypeKey = PropertyType | ComplexPropertyType | null;

export function getSingleSearchElementarySchoolDocId(resolvedAddresses: IAutocompleteEntry[]): string | null {
  if (resolvedAddresses && (resolvedAddresses.length === 1)) {
    const address = resolvedAddresses[0];
    if (address.type === CompletionType.SchoolDoc && address.schoolLevel === SchoolLevel.ELEMENTARY && address.docId) {
      return address.docId;
    }
  }
  return null;
}

export function getLeadingAgentsPositionByScreenRange(range: BreakpointValue, mode: SearchViewMode) {
  switch (mode) {
    case SearchViewMode.Grid: {
      switch (range) {
        case 1:
        case 2:
          return 7;
        case 3:
          return 13;
        default:
          return 17;
      }
    }
    case SearchViewMode.List: {
      switch (range) {
        case 1:
        case 2:
          return 7;
        default:
          return 9;
      }
    }
  }
}

export const getHomeTypeKey = (filters: IFiltersState, isCountrySearch: boolean): HomeTypeKey => {
  const { seller, propertyTypes } = filters;

  if (seller.length === 1 && seller[0] === Seller.Developer) {
    return ComplexPropertyType.Projects;
  }

  if (propertyTypes.length === 2 && propertyTypes[0] === PropertyType.PrivateHouse && propertyTypes[1] === PropertyType.Cottage) {
    return ComplexPropertyType.VillaOrCottage;
  }

  return propertyTypes.length === 1
    ? propertyTypes[0]
    : (!isCountrySearch && propertyTypes.length) ? ComplexPropertyType.MultiplePropertyType : null;
};

export function isDefaultSearch(resolvedAddresses: IAutocompleteEntry[]): boolean {
  if (resolvedAddresses && (resolvedAddresses.length === 1)) {
    return resolvedAddresses[0].docId === config.cityTerm;
  }
  return false;
}

export function getExposureCardInsertionIndex(screen: BreakpointValue, cardView: SearchViewMode): number {
  if (screen <= 2) {
    return 5; // mobile: after 6th item
  }
  return cardView === SearchViewMode.Grid ? 19 : 5; // grid: after 20th item (4th row); list: after 6th item
}

const PROJECTS_CAROUSEL_GRID_ROWS = 8;
const PROJECTS_CAROUSEL_LIST_ITEMS = 18;

// grid columns of ListingCardsItemModeB (see screens/List/styled.tsx)
const getGridColumns = (screen: BreakpointValue): number => {
  if (screen >= 4) return 3;
  if (screen >= 2) return 2;
  return 1;
};

export function getAdditionalProjectsCarouselInsertionIndex(
  screen: BreakpointValue,
  cardView: SearchViewMode,
  mode: SearchViewMode,
  itemsCount: number
): number {
  const itemsBefore = cardView === SearchViewMode.Grid
    ? PROJECTS_CAROUSEL_GRID_ROWS * getGridColumns(screen)
    : PROJECTS_CAROUSEL_LIST_ITEMS;

  const desired = Math.min(itemsBefore, itemsCount) - 1;
  if (desired < 0) return -1;

  // Clamping to a short page can land us on one of the sibling promo sections, which would stack
  // two full-width blocks in the same feed row. Step to the nearest free slot, trying later first
  // so we still land past the target row where the page allows it. The blog rail and madad banner
  // are deliberately not covered — their positions are decided inline in ListView.
  const taken = [
    getExposureCardInsertionIndex(screen, cardView),
    getLeadingAgentsPositionByScreenRange(screen, mode),
  ];
  const lastIndex = itemsCount - 1;

  for (let offset = 0; offset <= itemsCount; offset++) {
    const later = desired + offset;
    if (later <= lastIndex && taken.indexOf(later) === -1) return later;
    const earlier = desired - offset;
    if (earlier >= 0 && taken.indexOf(earlier) === -1) return earlier;
  }

  // unreachable while no sibling sits at index 0, which the loop relies on to always find a slot
  return desired;
}
const getSearchAddressText = (t: ITranslate, docs: IAutocompleteEntry[], isSingleAddress: boolean) => (
  isSingleAddress
    // the doc union is wider than the key's params; ICU only reads the fields relevant to the doc type
    ? t('searchListView.h1.addressFragment', { ...(docs[0] as any), cityCode: config.city })
    : docs && docs.reduce((acc, { name }, i) => `${acc}${i === 0 ? '' : t('searchListView.h1.addressFragment.multi.separator')}${name}`, '')
);

export interface ResidentialSearchPageH1Options {
  docs: IAutocompleteEntry[];
  filtersState: IFiltersState;
  dealType: DealType;
  isActiveImmediateSafeRoom: boolean;
  isOnlySeoAmenityFilter: boolean;
}

export const getResidentialSearchPageH1 = (t: ITranslate, options: ResidentialSearchPageH1Options): string => {
  const { docs, filtersState, dealType, isActiveImmediateSafeRoom, isOnlySeoAmenityFilter } = options;

  if (!filtersState || !dealType) return null;

  const { roomsRange, propertyTypes } = filtersState;
  const [ bedsFrom, bedsTo ] = roomsRange;

  const searchAreasCount = docs && docs.length;
  const isSingleAddress = searchAreasCount === 1;
  const resolvedAddress = isSingleAddress && docs[0];
  const isAddressSearch = isSingleAddress ? resolvedAddress.type === CompletionType.Address : false;
  const isCountrySearch = isSingleAddress ? resolvedAddress.type === CompletionType.Country : false;
  const addressText = getSearchAddressText(t, docs, isSingleAddress);

  const withSafeSpace = filtersState.amenities && filtersState.amenities.length && filtersState.amenities.includes(Amenity.SecureRoom) ? ' ' : null;
  const homeTypeText = t('searchListView.h1.homeTypeFragment', { homeType: getHomeTypeKey(filtersState, isCountrySearch) });
  const bedsText = t('searchListView.h1.bedsFragment', {
    bedsValue: bedsFrom !== null && bedsTo === null ? bedsFrom : null,
  }) || null;

  let propertyType: string = null;

  if (!isCountrySearch && propertyTypes) {
    propertyType = propertyTypes.length > 1
      ? t('searchListView.h.multi.fragment')
      : t('filters.propertyTypeOptions.plural', { value: propertyTypes[0] });
  }

  if (isAddressSearch) {
    return t('searchListView.h1.residential.address', {
      address: addressText,
      dealType,
      propertyType,
      withSafeSpace,
      isActiveImmediateSafeRoom,
    });
  }

  if (isOnlySeoAmenityFilter) {
    return t('searchListView.h1.onlySeoAmenityFilter', {
      address: addressText,
      dealType,
      amenity: filtersState.amenities[0],
    });
  }

  return t('searchListView.h1', {
    address: addressText,
    dealType,
    homeType: homeTypeText,
    beds: bedsText,
    withSafeSpace,
    isActiveImmediateSafeRoom,
  });
};

const COMMERCIAL_H1_ADDRESS_TYPES = new Set([ CompletionType.Address ]);

export interface CommercialSearchPageH1Options {
  docs: IAutocompleteEntry[];
  filtersState: IFiltersState;
  dealType: DealType;
  homeTypes: PropertyType[];
  seller: Seller;
  noFilters: boolean;
}

export const getCommercialSearchPageH1 = (t: ITranslate, options: CommercialSearchPageH1Options): string => {
  const { docs, filtersState, dealType, homeTypes, seller, noFilters } = options;

  if (!filtersState || !dealType) return null;

  const { propertyTypes } = filtersState;

  const searchAreasCount = docs && docs.length;
  const isSingleAddress = searchAreasCount === 1;
  const resolvedAddress = isSingleAddress && docs[0];
  const isSingleAddressSearch = isSingleAddress && COMMERCIAL_H1_ADDRESS_TYPES.has(docs[0].type);
  const withSafeSpace = filtersState.amenities && filtersState.amenities.length && filtersState.amenities.includes(Amenity.SecureRoom) ? ' ' : null;
  const addressText = getSearchAddressText(t, docs, isSingleAddress);
  const mappedDealTypeText = t('html.dealTypeFragment', { dealType });
  const singlePropertyTypeText = t('searchListView.commercial.single.propertyType', { propertyType: head(propertyTypes) });
  const mappedPropertyType = t('searchListView.commercial.multi.propertyType', { count: propertyTypes && propertyTypes.length || 0, propertyType: singlePropertyTypeText });
  const baseText = isSingleAddress
    ? t('searchListView.commercial.h1', {
      location: addressText,
      dealType: mappedDealTypeText,
      propertyType: mappedPropertyType,
      withSafeSpace,
    })
    : t('searchListView.commercial.h1.multi', {
      dealType: mappedDealTypeText,
      propertyType: mappedPropertyType,
    });

  let headingText = isSingleAddressSearch
    ? t('searchListView.h1.commercial.address', { address: docs[0].name, dealType, withSafeSpace })
    : baseText;

  const sellerFragment = seller ? t('filters.sellerOptions', { value: seller }) : null;
  const secondSinglePropertyTypeText = homeTypes && homeTypes.length > 1 ? t('searchListView.commercial.single.propertyType', { propertyType: homeTypes[1] }) : null;
  const propertiesStr = [ singlePropertyTypeText, secondSinglePropertyTypeText ].join(', ');

  if (resolvedAddress) {
    if (noFilters) {
      if (resolvedAddress.type === CompletionType.Country) {
        headingText = t('searchListView.h1.commercial.noFilters.country', { dealType: mappedDealTypeText });
      }
      else if (resolvedAddress.type === CompletionType.City) {
        headingText = t('searchListView.h1.commercial.noFilters.city', { dealType: mappedDealTypeText, city: addressText });
      }
    }
    else if (homeTypes && homeTypes.length && seller) {
      if (homeTypes.length === 1) {
        if (resolvedAddress.type === CompletionType.Country) {
          headingText = t('searchListView.h1.commercial.singlePropertyType.seller.country', {
            dealType: mappedDealTypeText,
            propertyType: singlePropertyTypeText,
            sellerFragment,
          });
        }
        else if (resolvedAddress.type === CompletionType.City) {
          headingText = t('searchListView.h1.commercial.singlePropertyType.seller.city', {
            dealType: mappedDealTypeText,
            sellerFragment,
            propertyType: singlePropertyTypeText,
            city: addressText,
          });
        }
      }
      else {
        if (resolvedAddress.type === CompletionType.Country) {
          headingText = t('searchListView.h1.commercial.multiPropertyType.seller.country', {
            dealType: mappedDealTypeText,
            propertiesStr,
            sellerFragment,
          });
        }
        else if (resolvedAddress.type === CompletionType.City) {
          headingText = t('searchListView.h1.commercial.multiPropertyType.seller.city', {
            dealType: mappedDealTypeText,
            city: addressText,
            sellerFragment,
            propertiesStr,
          });
        }
      }
    }
    else if (homeTypes && homeTypes.length) {
      if (homeTypes.length === 1) {
        if (resolvedAddress.type === CompletionType.Country) {
          headingText = t('searchListView.h1.commercial.singlePropertyType.country', {
            dealType: mappedDealTypeText,
            propertyType: singlePropertyTypeText,
          });
        }
      }
      else {
        if (resolvedAddress.type === CompletionType.Country) {
          headingText = t('searchListView.h1.commercial.multiPropertyType.country', {
            dealType: mappedDealTypeText,
            propertiesStr,
          });
        }
      }
    }
    else if (seller) {
      if (resolvedAddress.type === CompletionType.Country) {
        headingText = t('searchListView.h1.commercial.seller.country', {
          dealType: mappedDealTypeText,
          sellerFragment,
        });
      }
      else if (resolvedAddress.type === CompletionType.City) {
        headingText = t('searchListView.h1.commercial.seller.city', {
          dealType: mappedDealTypeText,
          sellerFragment,
          city: addressText,
        });
      }
    }
  }

  return headingText;
};
